import { useEffect, useMemo, useState } from "react";
import Shimmer from "./Shimmer.jsx";
import config from "../config/index.js";
import { useChainTime } from "../hooks/useChainTime.js";
import { formatUsdcCompact, liveAccruedInterest } from "../utils/format.js";

/**
 * Pre-mapped icon assets. The same four illustrations are reused on the
 * Dashboard, My TD's, and Loans pages — exporting the map here keeps
 * the keys consistent across all callers.
 */
export const STAT_ICONS = {
    holdings: "/images/holdingsAndPayable.png",
    interest: "/images/netAndInterest.png",
    safe: "/images/safeAndLTV.png",
    active: "/images/activeLoansAndTDs.png"
};

/**
 * Wallet-scoped headline stats shown at the top of the Dashboard and
 * My TD's pages. Reused so both surfaces stay in sync if we tweak
 * card styling, deltas or icon set later.
 */
export default function StatRow({ totals, activeCount, deposits = [], isLoading = false }) {
    // 1-second ticker so the live Net Yield / Total Holdings cards keep
    // advancing instead of freezing between data fetches.
    const [tick, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, []);
    // ONE shared timestamp for every derived figure this render.
    // Previously `day` and `liveInterest` each called `Date.now()`
    // separately, so they could land on different seconds — making the
    // live yield and the 24h-window yield drift by a few wei. That tiny
    // drift turned the reconstructed "24h ago" baseline into a non-zero
    // dust value instead of exactly 0, which blew the % delta up to
    // millions of percent. A single shared `nowSec` keeps them coherent.
    //
    // Sources from `useChainTime` so local-node testing (Hardhat
    // `evm_increaseTime`) shows correct accrual immediately after a
    // time warp — without it the UI keeps reading wall clock and
    // reports 0 elapsed days against the deposit's chain startTime.
    const { chainNowSec } = useChainTime();
    // eslint-disable-next-line no-unused-vars
    const _tick = tick; // dependency so the per-second re-render fires
    const nowSec = useMemo(() => chainNowSec, [chainNowSec, tick]);

    // Count of deposits opened within the trailing 24h — the one delta
    // that's still a genuine, meaningful figure (it drives the "Active
    // Term Deposits" card's activity sub-label).
    const newDeposits24h = useMemo(() => {
        let count = 0;
        for (const d of deposits) {
            if (Number(d.state) !== 1) continue; // Active only
            if (nowSec - Number(d.startTime) < 86400) count += 1;
        }
        return count;
    }, [deposits, nowSec]);

    // Accrued yield, computed the SAME way the contract does — per whole
    // elapsed day (`liveAccruedInterest`). The indexed `accruedInterest`
    // only moves on chain events so we replay the formula, but it must
    // match the contract's day-stepped figure — a per-second number
    // would show interest the contract hasn't actually credited yet.
    const liveInterest = useMemo(() => {
        let total = 0n;
        for (const d of deposits) {
            if (Number(d.state) !== 1) continue; // Active only
            // §V6.2 — real accrual rate = 20% for both products (Turbo's
            // 27.5% is a display-only label); use the stored rate.
            total += liveAccruedInterest(
                d.principal,
                d.startTime,
                d.maturityTime,
                nowSec,
                Number(d.annualRateBps) || config.protocol.annualRateBps,
                d.realizedInterest
            );
        }
        return total;
    }, [deposits, nowSec]);

    const safeBps = BigInt(config.protocol.safeVaultBps);
    const maxLtvBps = BigInt(config.protocol.maxLtvBps);
    const holdingsNow = totals.principal + liveInterest;
    // §V6.2 — BORROWABLE cap counts ONLY Standard deposits. Turbo
    // reverts user-driven loans (`NoBorrowingAgainstTurbo`), so
    // summing its principal into the cap would inflate the figure
    // with capital the user can't actually borrow against. Reusing
    // `totals.principal / totals.loan` (which span ALL deposits)
    // would mis-quote any wallet that holds Turbo + Standard mix.
    let standardPrincipal = 0n;
    let standardLoan = 0n;
    for (const d of deposits) {
        if (Number(d.state) !== 1) continue;        // Active only
        if (Number(d.productMode) === 1) continue;  // Turbo — no user borrow
        standardPrincipal += BigInt(d.principal || 0);
        standardLoan += BigInt(d.loanBalance || 0);
    }
    const borrowCap = (standardPrincipal * safeBps * maxLtvBps) / (10000n * 10000n);
    const borrowNow = borrowCap > standardLoan ? borrowCap - standardLoan : 0n;
    // Descriptive sub-labels — each states what its card's figure means,
    // which is more useful (and honest) than a trailing-24h percentage
    // that is meaningless while every position is younger than the
    // window. The Active card keeps a real 24h count since that delta
    // genuinely reflects activity.
    // §V6.2 — Net Yield aggregates across BOTH products (Standard 20%
    // APR, Turbo 27.5% APR). A single hard-coded percentage would
    // misquote any wallet holding Turbo deposits. Showing the static
    // protocol-wide range keeps the label honest without depending on
    // the user's specific mix.
    const aprRangeLabel = "20–27.5% APR";
    const maxLtvPct = config.protocol.maxLtvBps / 100;

    const items = [
        {
            label: "Total Holdings",
            value: `$${formatUsdcCompact(holdingsNow)}`,
            unit: "USDC",
            delta: "Principal + live yield",
            iconSrc: STAT_ICONS.holdings
        },
        {
            label: "Net Yield",
            value: `$${formatUsdcCompact(liveInterest)}`,
            unit: "USDC",
            delta: `Accruing · ${aprRangeLabel}`,
            iconSrc: STAT_ICONS.interest
        },
        {
            label: "Available to borrow",
            value: `$${formatUsdcCompact(borrowNow)}`,
            unit: "",
            delta: `Up to ${maxLtvPct}% LTV`,
            iconSrc: STAT_ICONS.safe
        },
        {
            label: "Active Term Deposits",
            value: String(activeCount),
            unit: "",
            delta:
                newDeposits24h > 0
                    ? `+${newDeposits24h} new · 24h`
                    : "All earning yield",
            iconSrc: STAT_ICONS.active
        }
    ];
    return (
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {items.map((item) => (
                <StatCard key={item.label} {...item} loading={isLoading} />
            ))}
        </div>
    );
}

export function StatCard({ label, value, unit, delta, icon, iconSrc, loading = false }) {
    return (
        <div className="rounded-[6px] bg-[#10281b] ring-[3px] ring-[#FBC206]/25 px-4 py-3">
            <div className="flex items-start gap-3">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/5 text-[#e6c985] overflow-hidden">
                    {iconSrc ? <MaskedIcon src={iconSrc} /> : icon}
                </span>
                <div className="flex-1">
                    <div className="flex items-center gap-1 font-manrope text-[11px] font-semibold text-[#e6c985]">
                        {label}
                        <InfoIcon />
                    </div>
                    <div className="mt-0.5 text-[9px] font-semibold text-[#22C55E]">
                        {loading ? <Shimmer width="2.5rem" height="0.6rem" tone="light" /> : delta}
                    </div>
                </div>
            </div>
            <div className="mt-2 flex items-baseline gap-2 font-manrope">
                {loading ? (
                    <Shimmer width="4rem" height="1.125rem" tone="light" />
                ) : (
                    <>
                        <span className="text-[18px] font-bold leading-none text-white">{value}</span>
                        {unit ? <span className="text-[10px] font-semibold text-white/60">{unit}</span> : null}
                    </>
                )}
            </div>
        </div>
    );
}

function InfoIcon() {
    return (
        <svg className="h-3 w-3 text-[#e6c985]/60" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
            <circle cx="8" cy="8" r="6" />
            <path d="M8 7v4" />
            <circle cx="8" cy="5" r="0.6" fill="currentColor" />
        </svg>
    );
}

/**
 * Renders a raster icon (PNG/SVG) as a CSS mask so its shape picks up
 * the parent's `currentColor`. The source assets were designed in dark
 * green for a light card — using them as a mask lets the same files
 * render in gold on the new dark-green card surface.
 */
function MaskedIcon({ src, size = 16 }) {
    return (
        <span
            aria-hidden="true"
            className="inline-block shrink-0"
            style={{
                width: size,
                height: size,
                backgroundColor: "currentColor",
                WebkitMaskImage: `url(${src})`,
                maskImage: `url(${src})`,
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskPosition: "center",
                maskPosition: "center",
                WebkitMaskSize: "contain",
                maskSize: "contain"
            }}
        />
    );
}

